import { useState } from 'react';
import type { PharmacyReview } from '@/mocks/pharmacies';

interface PharmacyReviewsProps {
  reviews: PharmacyReview[];
  rating: number;
  totalCount: number;
}

const StarRating = ({ value, size = 14 }: { value: number; size?: number }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map(i => (
      <i
        key={i}
        className={i <= Math.round(value) ? 'ri-star-fill' : 'ri-star-line'}
        style={{ color: '#f0ff00', fontSize: `${size}px` }}
      />
    ))}
  </div>
);

const ratingDist = [
  { stars: 5, pct: 72 },
  { stars: 4, pct: 18 },
  { stars: 3, pct: 6 },
  { stars: 2, pct: 2 },
  { stars: 1, pct: 2 },
];

const PharmacyReviews = ({ reviews, rating, totalCount }: PharmacyReviewsProps) => {
  const [helpfulIds, setHelpfulIds] = useState<number[]>([]);
  const [showAll, setShowAll] = useState(false);

  const displayed = showAll ? reviews : reviews.slice(0, 3);

  const toggleHelpful = (id: number) => {
    setHelpfulIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  return (
    <div>
      {/* Rating summary */}
      <div
        className="flex flex-col md:flex-row gap-6 p-6 rounded-2xl mb-6"
        style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}
      >
        {/* Big rating */}
        <div className="flex flex-col items-center justify-center min-w-[120px]">
          <div className="font-orbitron font-black text-5xl mb-2" style={{ color: '#f0ff00', textShadow: '0 0 20px rgba(240,255,0,0.3)' }}>
            {rating.toFixed(1)}
          </div>
          <StarRating value={rating} size={18} />
          <div className="font-mono text-xs text-white/35 mt-2">{totalCount} отзывов</div>
        </div>

        {/* Distribution bars */}
        <div className="flex-1 flex flex-col gap-2 justify-center">
          {ratingDist.map(({ stars, pct }) => (
            <div key={stars} className="flex items-center gap-3">
              <div className="flex items-center gap-1 w-12 shrink-0">
                <span className="font-mono text-xs text-white/50">{stars}</span>
                <i className="ri-star-fill text-xs" style={{ color: '#f0ff00' }} />
              </div>
              <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{ width: `${pct}%`, background: stars >= 4 ? '#39ff14' : stars === 3 ? '#f0ff00' : '#ff2d78' }}
                />
              </div>
              <span className="font-mono text-xs text-white/35 w-8 text-right shrink-0">{pct}%</span>
            </div>
          ))}
        </div>

        {/* Tags */}
        <div className="flex flex-col gap-2 justify-center">
          {[
            { label: 'Вежливый персонал', count: 312 },
            { label: 'Большой выбор', count: 287 },
            { label: 'Быстрое обслуживание', count: 198 },
            { label: 'Удобное расположение', count: 176 },
          ].map(tag => (
            <div
              key={tag.label}
              className="flex items-center justify-between gap-4 px-3 py-1.5 rounded-xl"
              style={{ background: 'rgba(57,255,20,0.05)', border: '1px solid rgba(57,255,20,0.12)' }}
            >
              <span className="font-mono text-xs text-white/60">{tag.label}</span>
              <span className="font-mono text-xs" style={{ color: '#39ff14' }}>{tag.count}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Review cards */}
      <div className="space-y-4">
        {displayed.map(review => (
          <div
            key={review.id}
            className="p-5 rounded-2xl transition-all duration-200"
            style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}
          >
            <div className="flex items-start gap-4">
              {/* Avatar */}
              <div className="w-10 h-10 rounded-full overflow-hidden shrink-0" style={{ border: '1px solid rgba(255,45,120,0.3)' }}>
                <img src={review.avatar} alt={review.author} className="w-full h-full object-cover" />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-3 mb-1.5">
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm text-white font-medium">{review.author}</span>
                    <StarRating value={review.rating} size={12} />
                  </div>
                  <span className="font-mono text-xs text-white/30 shrink-0">{review.date}</span>
                </div>
                <p className="font-mono text-sm text-white/60 leading-relaxed">{review.text}</p>

                {/* Helpful */}
                <div className="flex items-center gap-3 mt-3">
                  <span className="font-mono text-xs text-white/25">Полезный отзыв?</span>
                  <button
                    onClick={() => toggleHelpful(review.id)}
                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg font-mono text-xs cursor-pointer transition-all duration-200"
                    style={helpfulIds.includes(review.id)
                      ? { background: 'rgba(57,255,20,0.1)', border: '1px solid rgba(57,255,20,0.3)', color: '#39ff14' }
                      : { border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.35)' }
                    }
                  >
                    <i className="ri-thumb-up-line" />
                    {review.helpful + (helpfulIds.includes(review.id) ? 1 : 0)}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Show more */}
      {reviews.length > 3 && (
        <button
          onClick={() => setShowAll(v => !v)}
          className="whitespace-nowrap w-full mt-4 py-3 rounded-2xl font-mono text-sm cursor-pointer transition-all duration-200 flex items-center justify-center gap-2"
          style={{ border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.4)' }}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,45,120,0.3)'; (e.currentTarget as HTMLElement).style.color = '#ff2d78'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.08)'; (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.4)'; }}
        >
          <i className={showAll ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} />
          {showAll ? 'Скрыть отзывы' : `Показать все ${reviews.length} отзыва`}
        </button>
      )}
    </div>
  );
};

export default PharmacyReviews;
